import React, { useEffect } from 'react';
import clsx from 'clsx';
import InputField from '../../Forms/Input/InputField';
import TextAreaField from '../../Forms/Input/TextAreaField';
import SelectField from '../../Forms/Input/SelectField';
import SimpleSearchSelect from '../../Forms/Input/SimpleSearchSelect';
import { ICustomerForm } from '../../../types/customer/customerInterfaces';
import { useSupplier } from '../../../hooks/useSupplier';
import QuickAddEconomicActivity from '../../QuickAdd/EconomicActivity/QuickAddEconomicActivity';

interface CustomerJuridicoFormProps {
  form: ICustomerForm;
  updateField: (field: keyof ICustomerForm, value: any) => void;

  taxpayerCategoryDropdown: { id: number; name: string }[];
  economicActivitiesDropdown: { id: number; name: string }[];
  departments: { id: number; name: string; code: string }[];
  municipalities: { id: number; name: string }[];
  documentIdentificationTypeDropdown: {
    id: number;
    name: string;
    code: string;
  }[]; // 👈 añadido

  onFetchMunicipalities: (departmentCode: string) => void;
  onCancel?: () => void;
  onSubmit?: () => void;

  onEconomicActivityCreated: (data: { name: string }) => Promise<void> | void;
  onReloadEconomicActivities: () => void;

  isSubmitting?: boolean;
}

const SupplierJuridicoForm: React.FC<CustomerJuridicoFormProps> = ({
  form,
  updateField,
  taxpayerCategoryDropdown,
  economicActivitiesDropdown,
  departments,
  municipalities,
  documentIdentificationTypeDropdown,
  onFetchMunicipalities,
  onCancel,
  onSubmit,
  onEconomicActivityCreated,
  onReloadEconomicActivities,
  isSubmitting = false,
}) => {
  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = Number(e.target.value);
    updateField('departmentId', selectedId);
    updateField('municipalityId', 0);

    const dept = departments.find((d) => d.id === selectedId);
    if (dept) onFetchMunicipalities(dept.code);
  };

  useEffect(() => {
    if (form.departmentId && form.departmentCode) {
      onFetchMunicipalities(form.departmentCode);
    }
  }, [form.departmentId, form.departmentCode]);

  const { errors } = useSupplier();

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {/* 📌 Datos Generales */}
      <SelectField
        label="Tipo de Documento"
        name="documentIdentificationTypeId"
        iconKey="list"
        value={form.documentIdentificationTypeId ?? ''}
        required
        onChange={(e) => {
          const selectedId = Number(e.target.value);
          updateField('documentIdentificationTypeId', selectedId);

          // 👉 Busca el código asociado al ID seleccionado
          const selected = documentIdentificationTypeDropdown.find(
            (t) => t.id === selectedId
          );
          updateField('documentIdentificationTypeCode', selected?.code || '');
        }}
        options={documentIdentificationTypeDropdown.map((item) => ({
          value: item.id,
          label: item.name,
        }))}
        error={errors?.documentIdentificationTypeId}
      />

      {(() => {
        const selected = documentIdentificationTypeDropdown.find(
          (t) => t.id === form.documentIdentificationTypeId
        );
        const selectedCode = selected?.code;

        return (
          <>
            {/* Razón Social */}
            <InputField
              label="Razón Social"
              name="legalName"
              value={form.legalName}
              onChange={(e) => updateField('legalName', e.target.value)}
              type="text"
              placeholder="Ingrese la razón social"
              iconKey="user"
              error={errors?.legalName}
              required
            />

            {/* Nombre Comercial */}
            <InputField
              label="Nombre Comercial"
              name="tradeName"
              value={form.tradeName}
              onChange={(e) => updateField('tradeName', e.target.value)}
              type="text"
              placeholder="Ingrese el nombre comercial"
              iconKey="user"
              error={errors?.tradeName}
              required
            />

            {/* Condicionales según código */}
            {selectedCode === '36' && (
              <InputField
                label="NIT"
                name="nit"
                value={form.nit}
                onChange={(e) => updateField('nit', e.target.value)}
                type="text"
                placeholder="0000-000000-000-0"
                iconKey="idCard"
                error={errors?.nit}
              />
            )}

            {selectedCode === '13' && (
              <InputField
                label="DUI"
                name="dui"
                value={form.dui}
                onChange={(e) => updateField('dui', e.target.value)}
                type="text"
                placeholder="00000000-0"
                iconKey="idCard"
                error={errors?.dui}
              />
            )}

            {selectedCode === '03' && (
              <InputField
                label="Pasaporte"
                name="passportNumber"
                value={form.passportNumber}
                onChange={(e) => updateField('passportNumber', e.target.value)}
                type="text"
                iconKey="idCard"
                error={errors?.passportNumber}
              />
            )}

            {selectedCode === '02' && (
              <InputField
                label="Carnet de Residente"
                name="residentCardNumber"
                value={form.residentCardNumber}
                onChange={(e) =>
                  updateField('residentCardNumber', e.target.value)
                }
                type="text"
                iconKey="idCard"
                error={errors?.residentCardNumber}
              />
            )}

            {selectedCode === '37' && (
              <InputField
                label="Otra Identificación"
                name="otherIdentification"
                value={form.otherIdentification}
                onChange={(e) =>
                  updateField('otherIdentification', e.target.value)
                }
                iconKey="edit"
                type="text"
                error={errors?.otherIdentification}
              />
            )}
          </>
        );
      })()}

      {/* 📌 NRC siempre visible */}
      <InputField
        label="NRC"
        name="nrc"
        value={form.nrc}
        onChange={(e) => updateField('nrc', e.target.value)}
        type="text"
        placeholder="Número de registro (NRC)"
        iconKey="idCard"
        error={errors?.nrc}
      />

      {/* Teléfono */}
      <InputField
        label="Teléfono"
        name="phone"
        value={form.phone}
        onChange={(e) => updateField('phone', e.target.value)}
        type="text"
        placeholder="Ejemplo: 7777-7777"
        iconKey="phone"
        error={errors?.phone}
        required
      />

      {/* Correo */}
      <InputField
        label="Correo Electrónico"
        name="email"
        type="email"
        value={form.email}
        onChange={(e) => updateField('email', e.target.value)}
        placeholder="usuario@correo.com"
        iconKey="email"
        error={errors?.email}
        required
      />

      {/* 📌 Tipo de Contribuyente */}
      <div className="md:col-span-3">
        <div className="mb-6 border-b pb-4">
          <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
            Tipo de Contribuyente
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Selecciona la categoría fiscal de la empresa según los lineamientos
            del Ministerio de Hacienda. Esto determinará cómo se procesan sus
            obligaciones tributarias.
          </p>
        </div>
        <div className="max-w-md">
          <SelectField
            label="Categoría del Contribuyente"
            name="taxpayerCategoryId"
            value={form.taxpayerCategoryId}
            onChange={(e) =>
              updateField('taxpayerCategoryId', Number(e.target.value))
            }
            options={taxpayerCategoryDropdown.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            iconKey="list"
            error={errors?.taxpayerCategoryId}
            required
          />
        </div>
      </div>

      {/* 📌 Actividad Económica */}
      <div className="mb-6 border-b pb-4 md:col-span-3">
        <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
          Selecciona la Actividad Económica del Proveedor
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
          Selecciona la actividad económica principal del proveedor, según el
          catálogo oficial del Ministerio de Hacienda. Esto permitirá al sistema
          clasificar correctamente las operaciones del proveedor.
        </p>
        {/* ✅ Acción rápida (crear + recargar) */}
        <QuickAddEconomicActivity
          onActivityCreated={onEconomicActivityCreated}
          onReload={onReloadEconomicActivities}
        />

        {/* 🧾 Selector */}
        <div className="mt-2 flex items-end gap-3">
          <div className="flex-1">
            <SimpleSearchSelect
              label="Actividad Económica"
              name="economicActivityId"
              options={economicActivitiesDropdown}
              selectedValue={form.economicActivityId}
              required
              onSelect={(option) =>
                updateField('economicActivityId', option.id)
              }
              iconKey="listOrdered"
              error={errors?.economicActivityId}
            />
          </div>
        </div>
      </div>

      {/* 📌 Dirección */}
      <div className="mt-5 md:col-span-3">
        <div className="mb-6 border-b pb-4">
          <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
            Dirección
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Selecciona el departamento y municipio de la empresa, y agrega el
            complemento de la dirección según lineamientos del Ministerio de
            Hacienda.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <SelectField
            label="Departamento"
            name="departmentId"
            value={form.departmentId}
            onChange={handleDepartmentChange}
            options={departments.map((d) => ({
              value: d.id,
              label: d.name,
            }))}
            iconKey="list"
            error={errors?.departmentId}
            required
          />

          <SelectField
            label="Municipio"
            name="municipalityId"
            value={form.municipalityId}
            onChange={(e) =>
              updateField('municipalityId', Number(e.target.value))
            }
            options={municipalities.map((m) => ({
              value: m.id,
              label: m.name,
            }))}
            iconKey="list"
            error={errors?.municipalityId}
            required
          />
        </div>

        {/* Complemento de la dirección */}
        <div className="mt-6">
          <TextAreaField
            label="Complemento"
            name="complement"
            value={form.complement}
            onChange={(e) => updateField('complement', e.target.value)}
            iconKey="address"
            error={errors?.complement}
            required
          />
        </div>
      </div>

      {/* 📌 Botones */}
      {(onCancel || onSubmit) && (
        <div className="flex gap-4 md:col-span-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 rounded bg-[#DC3545] px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
            >
              Cancelar
            </button>
          )}
          {onSubmit && (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onSubmit}
              className={clsx(
                'flex-1 rounded px-4.5 py-2.5 font-medium text-white',
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary hover:bg-opacity-90'
              )}
            >
              {isSubmitting
                ? form.id
                  ? 'Actualizando...'
                  : 'Guardando...'
                : form.id
                ? 'Actualizar Proveedor'
                : 'Registrar Proveedor'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SupplierJuridicoForm;
